import React, { useEffect, useState, useRef } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download, Upload, Wrench } from 'lucide-react';

export default function SkillsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [skills, setSkills] = useState([]);
  const [groups, setGroups] = useState([]);
  const [quals, setQuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [s, g, q] = await Promise.all([hrAPI.listSkills(), hrAPI.listMaster('skill-groups'), hrAPI.listMaster('qualifications')]);
      setSkills(s.data || []); setGroups(g.data || []); setQuals(q.data || []);
    } catch { toast.error('Failed to load skills'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!dlg.name.trim()) { toast.error('Skill name is required'); return; }
    if (!dlg.skill_group_id) { toast.error('Select a skill group'); return; }
    if (!dlg.skill_type) { toast.error('Select Hard or Soft'); return; }
    setSaving(true);
    try {
      const payload = {
        name: dlg.name, skill_group_id: dlg.skill_group_id, skill_type: dlg.skill_type,
        requires_specific_qualification: !!dlg.requires_specific_qualification,
        required_qualification_id: dlg.requires_specific_qualification ? (dlg.required_qualification_id || null) : null,
      };
      if (dlg.id) await hrAPI.updateSkill(dlg.id, payload);
      else await hrAPI.createSkill(payload);
      toast.success('Skill saved'); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete skill "${row.name}"?`)) return;
    try { await hrAPI.deleteSkill(row.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await hrAPI.skillsTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'Skills_Import_Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await hrAPI.importSkills(fd);
      const d = res.data;
      toast.success(`Imported ${d.created_skills} skill(s), ${d.created_groups} group(s), skipped ${d.skipped}`);
      if (d.errors?.length) toast.warning(`${d.errors.length} row(s) had issues`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-5" data-testid="hr-skills-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><Wrench className="w-6 h-6 text-pink-600" /> Skills</h1>
          <p className="text-sm text-slate-500 mt-0.5">Basic skills, each linked to a group and classified as Hard or Soft</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <input type="file" accept=".xlsx" ref={fileRef} onChange={onImport} className="hidden" data-testid="hr-skills-import-input" />
            <Button size="sm" variant="outline" onClick={downloadTemplate} className="gap-1" data-testid="hr-skills-template"><Download className="w-4 h-4" /> Template</Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-1" data-testid="hr-skills-import"><Upload className="w-4 h-4" /> {importing ? 'Importing...' : 'Import'}</Button>
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => setDlg({ name: '', skill_group_id: '', skill_type: '' })} data-testid="hr-skills-add"><Plus className="w-4 h-4" /> Add Skill</Button>
          </div>
        )}
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Skill</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Group</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Type</TableHead>
                {canManage && <TableHead className="text-xs font-semibold text-slate-500 w-24 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow>
              ) : skills.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">No skills yet. Add or import from Excel.</TableCell></TableRow>
              ) : skills.map(s => (
                <TableRow key={s.id} data-testid={`hr-skill-row-${s.id}`}>
                  <TableCell className="text-sm font-medium">{s.name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{s.skill_group_name || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${s.skill_type === 'hard' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{s.skill_type === 'hard' ? 'Hard' : 'Soft'}</Badge>
                    {s.requires_specific_qualification && (
                      <Badge variant="outline" className="text-[10px] ml-1 bg-rose-50 text-rose-700 border-rose-200" data-testid={`hr-skill-qual-badge-${s.id}`}>Qualification required</Badge>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ id: s.id, name: s.name, skill_group_id: s.skill_group_id, skill_type: s.skill_type, requires_specific_qualification: !!s.requires_specific_qualification, required_qualification_id: s.required_qualification_id || '' })} data-testid={`hr-skill-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(s)} data-testid={`hr-skill-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="hr-skill-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
            <DialogDescription className="text-xs">Hard skills are evaluated via test/practical; soft skills are observed.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Skill Name *</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} data-testid="hr-skill-name-input" autoFocus /></div>
              <div>
                <Label className="text-xs">Skill Group *</Label>
                <Select value={dlg.skill_group_id || undefined} onValueChange={v => setDlg({ ...dlg, skill_group_id: v })}>
                  <SelectTrigger data-testid="hr-skill-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
                {groups.length === 0 && <p className="text-[10px] text-amber-600 mt-1">No skill groups. Create one under Skill Groups first.</p>}
              </div>
              <div>
                <Label className="text-xs">Type *</Label>
                <Select value={dlg.skill_type || undefined} onValueChange={v => setDlg({ ...dlg, skill_type: v })}>
                  <SelectTrigger data-testid="hr-skill-type-select"><SelectValue placeholder="Hard or Soft" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hard">Hard (evaluated by test/practical)</SelectItem>
                    <SelectItem value="soft">Soft (observed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border rounded-md p-2.5 bg-slate-50/60 space-y-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-pink-600"
                    checked={!!dlg.requires_specific_qualification}
                    onChange={e => setDlg({ ...dlg, requires_specific_qualification: e.target.checked, required_qualification_id: e.target.checked ? dlg.required_qualification_id : '' })}
                    data-testid="hr-skill-requires-qual"
                  />
                  Specific qualification required
                </label>
                <p className="text-[10px] text-slate-500">Marks this skill as <b>critical</b> in the F/HR/02 matrix, so a backup person / plan must be maintained.</p>
                {dlg.requires_specific_qualification && (
                  <Select value={dlg.required_qualification_id || undefined} onValueChange={v => setDlg({ ...dlg, required_qualification_id: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="hr-skill-qual-select"><SelectValue placeholder="Qualification (optional)" /></SelectTrigger>
                    <SelectContent>{quals.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-skill-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
